// Single shared Google Maps JS loader.
//
// Two things the naive `<script>` approach gets wrong, both of which we hit:
//  1. Auth failures (RefererNotAllowedMapError, billing disabled, bad key) do
//     NOT fire script.onerror — Google reports them via window.gm_authFailure.
//     Without handling that, the UI shows a silent blank grey box.
//  2. Mounting several map/autocomplete widgets injects the script repeatedly.
// Everything here resolves once and is shared.

const SCRIPT_ID = "gmaps-script";
const LOAD_TIMEOUT_MS = 10000;

let loadPromise = null;
let authFailed = false;

export const apiKey = () => process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

export const mapsConfigured = () => Boolean(apiKey());

// True once Google has rejected our key — callers fall back instead of
// rendering an empty map.
export const mapsAuthFailed = () => authFailed;

export function loadGoogleMaps() {
  if (authFailed) return Promise.reject(new Error("google-maps-auth-failed"));
  if (!mapsConfigured()) return Promise.reject(new Error("google-maps-not-configured"));
  if (window.google?.maps) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    // Google calls this on any key/billing/referrer rejection.
    window.gm_authFailure = () => {
      authFailed = true;
      loadPromise = null;
      window.dispatchEvent(new Event("gmaps-auth-failed"));
      reject(new Error("google-maps-auth-failed"));
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      const started = Date.now();
      const iv = setInterval(() => {
        if (window.google?.maps) { clearInterval(iv); resolve(window.google); }
        else if (authFailed) { clearInterval(iv); reject(new Error("google-maps-auth-failed")); }
        else if (Date.now() - started > LOAD_TIMEOUT_MS) {
          clearInterval(iv); reject(new Error("google-maps-timeout"));
        }
      }, 100);
      return;
    }

    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey()}&libraries=places&loading=async`;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.google?.maps ? resolve(window.google) : reject(new Error("google-maps-missing")));
    s.onerror = () => { loadPromise = null; reject(new Error("google-maps-network")); };
    document.head.appendChild(s);
    setTimeout(() => { if (!window.google?.maps) reject(new Error("google-maps-timeout")); }, LOAD_TIMEOUT_MS);
  });

  return loadPromise;
}

// Places moved to importLibrary; older builds still expose the namespace
// directly. Try both, and let the caller degrade if neither exists.
export async function loadPlaces() {
  const google = await loadGoogleMaps();
  if (google.maps.importLibrary) {
    try {
      return await google.maps.importLibrary("places");
    } catch (e) {
      if (google.maps.places) return google.maps.places;
      throw e;
    }
  }
  if (google.maps.places) return google.maps.places;
  throw new Error("google-places-unavailable");
}
